// Fix für Category Name Typ-Fehler in AchievementHeader
import React, { useState } from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { getBungieIconUrl } from '../../services/bungieIconService';

interface AchievementHeaderProps {
  name: string;
  description: string;
  iconUrl?: string | null;
  isCompleted: boolean;
  categoryName?: string | null; // Hier wurde der Typ auf string | null | undefined geändert
  points: number;
  progress?: {
    current: number;
    total: number;
    percentage: number;
  };
  showProgress?: boolean;
}

const AchievementHeader: React.FC<AchievementHeaderProps> = ({
  name,
  description,
  iconUrl,
  isCompleted,
  categoryName,
  points,
  progress,
  showProgress = true
}) => {
  const { t } = useLanguage();
  const [iconError, setIconError] = useState(false);

  // Provide default values for progress if not provided
  const safeProgress = progress || { current: 0, total: 1, percentage: 0 };
  
  // Ensure we have valid numbers
  const percentage = isCompleted ? 100 : (safeProgress.percentage ?? 0);
  const current = safeProgress.current ?? 0;
  const total = safeProgress.total ?? 1;
  
  // Get Bungie icon URL through our proxy
  const iconSrc = !iconError && iconUrl ? getBungieIconUrl(iconUrl) : null;
  
  // Generate a placeholder color based on the achievement name for fallback
  const getPlaceholderColor = () => {
    const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const hue = hash % 360;
    return `hsl(${hue}, 70%, 30%)`;
  };

  // Sicherer Umgang mit categoryName, der null sein kann
  const displayCategoryName = categoryName || t('achievements.uncategorized');

  return (
    <div className="bg-gray-800 rounded-lg p-6 mb-6">
      <div className="flex items-start">
        {iconSrc ? (
          <img 
            src={iconSrc} 
            alt={name} 
            className="w-16 h-16 mr-4 bg-gray-700 rounded-md"
            onError={(e) => {
              setIconError(true);
              const target = e.target as HTMLImageElement;
              target.onerror = null;
              target.style.backgroundColor = getPlaceholderColor();
              target.src = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='; // Transparent 1x1 pixel
            }}
          />
        ) : (
          <div 
            className="w-16 h-16 mr-4 rounded-md flex items-center justify-center text-white font-bold text-xl"
            style={{ backgroundColor: getPlaceholderColor() }}
          >
            {name.charAt(0)}
          </div>
        )}
        <div>
          <div className="flex items-center">
            <h1 className="text-2xl font-bold text-destiny-gold">{name}</h1>
            {showProgress && isCompleted && <CheckCircleIcon className="h-6 w-6 text-destiny-green ml-2" />}
          </div>
          <p className="text-gray-300 mt-2">{description}</p>
          <div className="flex items-center mt-3">
            <span className="bg-gray-700 px-3 py-1 rounded-full text-destiny-gold text-sm">
              {displayCategoryName}
            </span>
            <span className="ml-3 text-white">{points} {t('achievements.points')}</span>
          </div>
        </div>
      </div>

      {/* Only show if showProgress is true */}
      {showProgress && (
        <div className="mt-6">
          <div className="flex justify-between mb-1 text-sm text-gray-300">
            <span>
              {isCompleted
                ? t('achievements.completed')
                : `${t('achievements.progress')}: ${current}/${total}`}
            </span>
            <span className="text-destiny-gold">{percentage}%</span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2.5">
            <div
              className={`${
                isCompleted ? 'bg-destiny-green' : 'bg-destiny-blue'
              } h-2.5 rounded-full`}
              style={{ width: `${percentage}%` }}
            ></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AchievementHeader;